import { EditClientSchema } from '@/validations/client';
import { IonContent, IonList, IonPage, IonText } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';

// Styles
import styles from '@/pages/Clients/Clients.module.css';

// Components
import { Button } from '@/components/Button';
import { ClientCard } from '@/components/Cards/ClientCard';
import { ClientOverviewHeader } from '@/components/ClientHeaders';
import { ClientModalContent } from '@/components/Clients/ClientModalContent';
import { EmptyClientsState } from '@/components/Clients/EmptyClientState';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { CustomLoading } from '@/components/Loading';
import { Modal } from '@/components/Modal/Modal';
import { NoInternet } from '@/components/NoInternet/NoInternet';
import { toast } from '@/components/Toaster/Toast';

import UserService from '@/hooks/useClients/userService';
import { useClientSearch } from '@/hooks/useClientSearch';
import { useDatabase } from '@/hooks/useDatabase';
import { useIsOnline } from '@/hooks/useIsOnline';

import { useAuthContext } from '@/contexts/authContext';

import type { ExportedClient } from '@/models';

// TODO: Add session info for last session date
export const ClientsOverviewPage = () => {
  const user = useAuthContext().state.userState?.user;
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getFirebaseClientByClientId } = useDatabase();
  const isOnline = useIsOnline();

  const [clientsList, setClientsList] = useState<ExportedClient[]>([]);

  useEffect(() => {
    setIsLoading(true);

    const unsubscribe = UserService.getClients(user!.uid, (clients) => {
      setClientsList(clients);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const searchOptions = {
    includeScore: true,
    useExtendedSearch: true,
    threshold: 0.2,
    keys: ['firstName', 'lastName', 'email'],
  };

  const { onSearch, query, searchResult } = useClientSearch<ExportedClient>(searchOptions, clientsList);

  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: new Date().toISOString(),
    email: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Handle input change and reset errors when the user types
  const handleInputChange = (field: string, value: string) => {
    setClientData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = { firstName: '', lastName: '', email: '' };
    let isValid = true;

    if (!clientData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }

    if (!clientData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!clientData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(clientData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setClientData({ firstName: '', lastName: '', dateOfBirth: new Date().toISOString(), email: '' });
    setErrors({ firstName: '', lastName: '', email: '' });
  };

  // Handle Confirm button
  const handleConfirm = async () => {
    if (validateForm()) {
      setCreatingClient(true);

      try {
        await UserService.addClient(clientData);
        setIsAddClientModalOpen(false);

        toast({
          variant: 'success',
          title: 'Client added successfully',
        });
      } catch (error: any) {
        if (error.message === 'A user with this email already exists.') {
          setErrors((prev) => ({ ...prev, email: error.message }));
        } else {
          toast({
            variant: 'error',
            title: 'Error adding client',
            description: error.message,
          });
        }
      } finally {
        setCreatingClient(false);
        resetForm();
      }
    }
  };

  const handleCloseModal = () => {
    setIsAddClientModalOpen(false);
    resetForm();
  };

  /// MARK: Edit client logic

  const initialState = {
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
  };

  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [editClientId, setEditClientId] = useState<string | null>(null);
  const [editClientData, setEditClientData] = useState(initialState);
  const [editErrors, setEditErrors] = useState(initialState);

  const [isLoadingEditClient, setIsLoadingEditClient] = useState(false);

  const handleOpenEditModal = async (clientId: string) => {
    setEditClientId(clientId);
    setIsLoadingEditClient(true);
    setIsEditClientModalOpen(true);

    const editClient = await getFirebaseClientByClientId(clientId);

    if (!editClient) {
      handleCloseEditModal();
      toast({ variant: 'error', title: 'Error getting client', description: 'Client not found' });

      return null;
    }

    setEditClientData({
      firstName: editClient?.firstName ?? '',
      lastName: editClient?.lastName ?? '',
      dateOfBirth: new Date(editClient?.dateOfBirth.seconds * 1000).toISOString(),
      email: editClient?.email ?? '',
    });

    setIsLoadingEditClient(false);
  };

  const handleCloseEditModal = () => {
    setIsEditClientModalOpen(false);
    setEditClientId(null);
    setEditClientData(initialState);
    setEditErrors(initialState);
    setIsLoadingEditClient(false);
  };

  const handleEditSubmit = async () => {
    if (!editClientId) {
      toast({
        variant: 'error',
        title: 'Error updating client',
        description: "Something went wrong. Please contact support and mention error code 'ERR_CLIENTID_UNDEFINED'",
      });

      return;
    }

    const validatedData = EditClientSchema.safeParse(editClientData);

    if (!validatedData.success) {
      setEditErrors({
        firstName: validatedData.error.issues.find((issue) => issue.path[0] === 'firstName')?.message ?? '',
        lastName: validatedData.error.issues.find((issue) => issue.path[0] === 'lastName')?.message ?? '',
        email: validatedData.error.issues.find((issue) => issue.path[0] === 'email')?.message ?? '',
        dateOfBirth: '',
      });

      return;
    }

    try {
      await UserService.updateClient(editClientId, validatedData.data);
      handleCloseEditModal();

      toast({
        variant: 'success',
        title: 'Client edit was successful',
        description: 'The client info has been updated',
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast({
          variant: 'error',
          title: 'Error updating client',
          description: error.message,
        });
      }
    }
  };

  const handleEditInputChange = (field: string, value: string) => {
    setEditClientData((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const hasAnyError = useMemo(() => {
    return !!editErrors.email || !!editErrors.firstName || !!editErrors.lastName || !!editErrors.dateOfBirth;
  }, [editErrors.email, editErrors.firstName, editErrors.lastName, editErrors.dateOfBirth]);

  return (
    <>
      <Modal
        className={styles.addClientModal}
        footer={
          <>
            <Button onClick={handleCloseModal} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={handleConfirm} variant='primary'>
              Add client
            </Button>
          </>
        }
        header='Add new client'
        isOpen={isAddClientModalOpen}
        onDidDismiss={handleCloseModal}>
        <ClientModalContent
          clientData={clientData}
          errors={errors}
          loading={creatingClient}
          mode='create'
          onInputChange={handleInputChange}
        />
      </Modal>

      <Modal
        footer={
          <>
            <Button onClick={handleCloseEditModal} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} variant='primary'>
              Confirm
            </Button>
          </>
        }
        header='Edit client'
        isOpen={isEditClientModalOpen}
        onDidDismiss={handleCloseEditModal}
        style={{
          '--height': hasAnyError ? '375px' : '330px',
        }}>
        <ClientModalContent
          clientData={editClientData}
          errors={editErrors}
          loading={isLoadingEditClient}
          mode='edit'
          onInputChange={handleEditInputChange}
        />
      </Modal>

      <IonPage id='main'>
        <div className={styles.header}>
          <ClientOverviewHeader title={'Clients'}>
            <Button
              className={styles.actionButton}
              leadingIcon='plus'
              onClick={() => setIsAddClientModalOpen(true)}
              size='medium'
              variant='primary'>
              Add new client
            </Button>
          </ClientOverviewHeader>
        </div>

        {clientsList.length > 0 && (
          <div className={styles.tableHeader}>
            <Input
              className={styles.searchbar}
              isSearch
              leadingIcon='search'
              onIonInput={onSearch}
              placeholder='Search name, email...'
              value={query}
              variant='secondary'
            />

            <div className={styles.label}>
              <Icon color='var(--ion-color-grey400' name='calendar' />
              <IonText className={styles.labelText}>Date of Birth</IonText>
            </div>
            <div className={styles.label}>
              <Icon color='var(--ion-color-grey400' name='clock' />
              <IonText className={styles.labelText}>Last recording</IonText>
            </div>
          </div>
        )}

        <IonContent className={styles.content} color='light' id='main'>
          {!isOnline ? (
            <div className={styles.loading}>
              <NoInternet
                description='We could not load your data, make sure you have a stable internet connection'
                title='You are currently offline'
              />
            </div>
          ) : isLoading ? (
            <div className={styles.loading}>
              <CustomLoading />
            </div>
          ) : searchResult.length === 0 ? (
            <EmptyClientsState isSearching={query.length > 0} query={query} />
          ) : (
            <IonList className={styles.cards}>
              {searchResult.map((client) => (
                <ClientCard client={client} key={client.id} onEditClick={handleOpenEditModal} />
              ))}
            </IonList>
          )}
        </IonContent>
      </IonPage>
    </>
  );
};
